import React from 'react';
import { Sparkles, Tag, Clock, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { dailySpecials } from '../data/mock';

const SpecialsSection = () => {
  const getBadgeColor = (badge) => {
    switch (badge) {
      case 'HOT':
        return 'bg-[#ff6b6b] text-white';
      case 'SALE':
        return 'bg-[#00d4aa] text-white';
      case 'LIMITED':
        return 'bg-[#ffd700] text-[#1e1919]';
      case 'TRENDING':
        return 'bg-[#61525a] text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  return (
    <section id="specials" className="relative py-20 lg:py-28 bg-gradient-to-b from-[#1e1919] to-[#2d1f1f]">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffd700' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-[#ffd700]/10 backdrop-blur-sm border border-[#ffd700]/30 rounded-full px-4 py-2 mb-6">
            <Sparkles className="w-4 h-4 text-[#ffd700]" />
            <span className="text-[#ffd700] font-medium text-sm">Today's Hot Deals</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Specials of the <span className="text-[#ffd700]">Day</span>
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Don't miss out on these amazing deals! Limited time offers on our most popular products.
          </p>
          <div className="flex items-center justify-center gap-2 mt-4 text-[#ff6b6b]">
            <Clock className="w-5 h-5" />
            <span className="font-medium">Offers end in: 23:45:12</span>
          </div>
        </div>

        {/* Specials Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {dailySpecials.map((special) => (
            <Card
              key={special.id}
              className="group bg-gradient-to-br from-white/10 to-white/5 border-white/10 hover:border-[#ffd700]/50 transition-all duration-500 hover:shadow-2xl hover:shadow-[#ffd700]/10 overflow-hidden"
            >
              <CardContent className="p-6">
                {/* Badge */}
                <Badge className={`${getBadgeColor(special.badge)} mb-4 font-bold`}>
                  {special.badge}
                </Badge>
                
                {/* Content */}
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#ffd700] transition-colors">
                  {special.title}
                </h3>
                <p className="text-white/60 text-sm mb-4 line-clamp-2">
                  {special.description}
                </p>
                
                {/* Pricing */}
                <div className="flex items-end gap-3 mb-4">
                  <span className="text-3xl font-bold text-[#00d4aa]">
                    ${special.salePrice}
                  </span>
                  <span className="text-lg text-white/40 line-through">
                    ${special.originalPrice}
                  </span>
                </div>
                
                {/* Savings */}
                <div className="flex items-center gap-2 mb-4">
                  <Tag className="w-4 h-4 text-[#ff6b6b]" />
                  <span className="text-[#ff6b6b] text-sm font-medium">
                    Save ${(special.originalPrice - special.salePrice).toFixed(2)}
                  </span>
                </div>
                
                {/* CTA */}
                <Button className="w-full bg-white/10 hover:bg-[#ffd700] text-white hover:text-[#1e1919] font-semibold transition-all duration-300 group-hover:scale-[1.02]">
                  Add to Cart
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center mt-12">
          <Button
            size="lg"
            variant="outline"
            className="border-2 border-[#ffd700] text-[#ffd700] hover:bg-[#ffd700]/10 font-bold px-8"
          >
            View All Specials
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default SpecialsSection;
